'use client'

// React Imports
import { useState, useEffect } from 'react'

// Next Imports
import Link from 'next/link'
import { useParams, useRouter } from 'next/navigation'

// MUI Imports
import useMediaQuery from '@mui/material/useMediaQuery'
import { styled, useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Button from '@mui/material/Button'

// Third-party Imports
import classnames from 'classnames'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

// Type Imports
import toast from 'react-hot-toast'

import type { SystemMode } from '@core/types/types'
import type { Locale } from '@configs/i18n'

// Component Imports
import Logo from '@components/layout/shared/Logo'
import CustomTextField from '@core/components/mui/TextField'

// Hook Imports
import { useImageVariant } from '@core/hooks/useImageVariant'
import { useSettings } from '@core/hooks/useSettings'
import { useAuth } from '@core/contexts/authContext'
import { useRequireGuest } from '@core/hooks/useAuthGuard'

// Util Imports
import { getLocalizedUrl } from '@core/utils/i18n'

// i18n
import i18n, { t } from '@/core/i18n/setting'
import { useAPI } from '@/core/hooks/useAPI'
import { loginPasswordSchema } from '@/core/schema/auth-schema'

// import LanguageDropdown from '@/core/components/layout/shared/LanguageDropdown'

// Validation Schema Factory
const createRegisterSchema = () =>
  z.object({
    email: z.string().email(t('emailInvalid')).min(1, t('emailRequired')),
    password: loginPasswordSchema()
  })

type RegisterFormData = z.infer<ReturnType<typeof createRegisterSchema>>

// Styled Custom Components
const RegisterIllustration = styled('img')(({ theme }) => ({
  zIndex: 2,
  blockSize: 'auto',
  maxBlockSize: 600,
  maxInlineSize: '100%',
  margin: theme.spacing(12),
  [theme.breakpoints.down(1536)]: {
    maxBlockSize: 550
  },
  [theme.breakpoints.down('lg')]: {
    maxBlockSize: 450
  }
}))

const MaskImg = styled('img')({
  blockSize: 'auto',
  maxBlockSize: 345,
  inlineSize: '100%',
  position: 'absolute',
  insetBlockEnd: 0,
  zIndex: -1
})

const Register = ({ mode }: { mode: SystemMode }) => {
  // States
  const [isPasswordShown, setIsPasswordShown] = useState(false)
  const [registerSchema, setRegisterSchema] = useState(() => createRegisterSchema())

  // Protect this page - redirect authenticated users
  useRequireGuest('/home')

  // React Hook Form
  const {
    control,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: '',
      password: ''
    }
  })

  // Update schema when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      setRegisterSchema(createRegisterSchema())
    }

    i18n.on('languageChanged', handleLanguageChange)

    return () => {
      i18n.off('languageChanged', handleLanguageChange)
    }
  }, [])

  // Vars
  const darkImg = '/images/pages/auth-mask-dark.png'
  const lightImg = '/images/pages/auth-mask-light.png'
  const darkIllustration = '/images/illustrations/auth/v2-register-dark.png'
  const lightIllustration = '/images/illustrations/auth/v2-register-light.png'
  const borderedDarkIllustration = '/images/illustrations/auth/v2-register-dark-border.png'
  const borderedLightIllustration = '/images/illustrations/auth/v2-register-light-border.png'

  // Hooks
  const { lang: locale } = useParams()
  const { settings } = useSettings()
  const theme = useTheme()
  const hidden = useMediaQuery(theme.breakpoints.down('md'))
  const authBackground = useImageVariant(mode, lightImg, darkImg)

  const { mutateAsync, isPending } = useAPI(['auth', 'register'], {
    skipGlobalErrorHandler: true,
    onError(error: any) {
      const errors = Array.isArray(error.response?.data)
        ? (error.response.data as { code: string; description: string }[])
        : []

      const uniqueErrors = Array.from(new Map(errors.map(err => [err.code, err])).values())
      const textError = uniqueErrors.map(err => t(err.code as any))

      toast.error(
        <div>
          {textError.map((msg, i) => (
            <div key={i}>{msg}</div>
          ))}
        </div>
      )
    }
  })

  const { push } = useRouter()
  const { login } = useAuth()

  const characterIllustration = useImageVariant(
    mode,
    lightIllustration,
    darkIllustration,
    borderedLightIllustration,
    borderedDarkIllustration
  )

  const handleClickShowPassword = () => setIsPasswordShown(show => !show)

  // Form Submit Handler
  const onSubmit = async (data: RegisterFormData) => {
    try {
      const response = await mutateAsync(data)
      const token = response.data.token

      // Check if token exists before proceeding
      if (token) {
        // Store token in cookie using auth context
        // Create basic user object from registration data
        login(token, {
          id: Date.now().toString(), // Temporary ID until we get user data from API
          email: data.email
        })

        // Redirect to home page
        push('/home')
      } else {
        console.error('No token received from server')
      }
    } catch (error) {
      console.error('Registration error:', error)
    }
  }

  const passwordRules = [
    {
      label: t('passwordMinLength'),
      test: (value: string) => value.length >= 8
    },
    {
      label: t('passwordMustContainUppercase'),
      test: (value: string) => /[A-Z]/.test(value)
    },
    {
      label: t('passwordMustContainLowercase'),
      test: (value: string) => /[a-z]/.test(value)
    },
    {
      label: t('passwordMustContainNumber'),
      test: (value: string) => /\d/.test(value)
    },
    {
      label: t('passwordMustContainSpecialChar'),
      test: (value: string) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(value)
    }
  ]

  return (
    <div className='flex bs-full justify-center'>
      <div
        className={classnames(
          'flex bs-full items-center justify-center flex-1 min-bs-[100dvh] relative p-6 max-md:hidden',
          {
            'border-ie': settings.skin === 'bordered'
          }
        )}
      >
        <RegisterIllustration src={characterIllustration} alt={t('characterImage')} />
        {!hidden && <MaskImg alt={t('mask')} src={authBackground} />}
      </div>
      <div className='flex justify-center items-center bs-full bg-backgroundPaper !min-is-full p-6 md:!min-is-[unset] md:p-12 md:is-[480px]'>
        <Link
          href={getLocalizedUrl('/login', locale as Locale)}
          className='absolute block-start-5 sm:block-start-[33px] inline-start-6 sm:inline-start-[38px]'
        >
          <Logo />
        </Link>
        <div className='flex flex-col gap-6 is-full sm:is-auto md:is-full sm:max-is-[400px] md:max-is-[unset] mbs-8 sm:mbs-11 md:mbs-0'>
          {/*<div className='absolute top-5 inline-end-5'>*/}
          {/*  <LanguageDropdown />*/}
          {/*</div>*/}
          <div className='flex flex-col gap-1'>
            <Typography variant='h4'>{t('registerTitle')}</Typography>
            <Typography>{t('registerSubtitle')}</Typography>
          </div>
          <form noValidate autoComplete='off' onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-6'>
            <Controller
              name='email'
              control={control}
              render={({ field }) => (
                <CustomTextField
                  {...field}
                  fullWidth
                  label={t('registerEmail')}
                  placeholder={t('registerEmailPlaceholder')}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
              )}
            />
            <Controller
              name='password'
              control={control}
              render={({ field }) => (
                <div>
                  <CustomTextField
                    {...field}
                    fullWidth
                    label={t('password')}
                    placeholder={t('passwordPlaceholder')}
                    type={isPasswordShown ? 'text' : 'password'}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position='end'>
                            <IconButton
                              edge='end'
                              onClick={handleClickShowPassword}
                              onMouseDown={e => e.preventDefault()}
                            >
                              <i className={isPasswordShown ? 'tabler-eye-off' : 'tabler-eye'} />
                            </IconButton>
                          </InputAdornment>
                        )
                      }
                    }}
                  />
                  {
                    <div className='text-xs flex flex-col gap-1 mx-1 mt-2 leading-tight text-textSecondary'>
                      {passwordRules.map((rule, idx) => {
                        const passed = rule.test(field.value)

                        return (
                          <div
                            key={idx}
                            className={classnames('flex gap-1 items-center justify-start', passed && 'text-green-300')}
                          >
                            <i
                              className={classnames(
                                'text-xs',
                                passed ? 'tabler-check text-green-500' : 'tabler-x text-red-600'
                              )}
                            />
                            {rule.label}
                          </div>
                        )
                      })}
                    </div>
                  }
                </div>
              )}
            />

            <Button fullWidth variant='contained' type='submit' disabled={!isValid || isPending}>
              {isPending ? t('signing') : t('signUp')}
            </Button>
            <div className='flex justify-center items-center flex-wrap gap-2'>
              <Typography>{t('alreadyHaveAccount')}</Typography>
              <Typography component={Link} href={getLocalizedUrl('/login', locale as Locale)} color='primary.main'>
                {t('signInInstead')}
              </Typography>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default Register
